import { useState, useEffect, useCallback } from "react";
import { Pin, ChevronsRight } from "lucide-react";
import { useViewportCtx } from "@/contexts/ViewportCtx";
import { useDocumentCtx } from "@/contexts/DocumentCtx";
import { useWorkspaceCtx } from "@/contexts/WorkspaceCtx";
import { useAuth } from "@clerk/clerk-react";
import TaitoraUserButton from "@/components/TaitoraUserButton";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { SB } from "@/config/sidebarSizing";
import { THEME } from "@/config/theme";
import ProjectRail from "./ProjectRail";
import WorkspaceList from "./WorkspaceList";
import WorkspaceView from "./WorkspaceView";
import ConfirmDialog from "./ConfirmDialog";
import {
  listProjects, createProject, renameProject, deleteProject, setProjectIcon,
  createSubject, renameSubject, deleteSubject,
  createForge, renameForge, deleteForge,
  type ApiSubject, type ApiForge,
} from "@/services/projectService";
import { useCanvasStore } from "@/store/useCanvasStore";

const WORKSPACE_DEFAULT = 220;
const MOBILE_TREE_W = 256; // mobile tree overlay width (Tailwind w-64)

const TREE_LOCK_KEY = "kf:treeLocked";
// Desktop hover/pin rail: persist whether the panel is pinned open (mock `railPinned`).
const RAIL_PIN_KEY = "kf:railPinned";

type ConfirmPending = {
  title: string;
  message: string;
  action: () => Promise<void>;
} | null;

interface SidebarProps {
  isMobile: boolean;
  onProjectsLoaded?: () => void;
}

export default function Sidebar({ isMobile, onProjectsLoaded }: SidebarProps) {
  const { getToken } = useAuth();
  const reduce = useReducedMotion();
  const { forgeId, setForgeId } = useViewportCtx();
  const { setDoc } = useDocumentCtx();
  // Workspace list, active selection, and tree open-state live in WorkspaceCtx
  // so the canvas onboarding guide can read the same funnel state (and defer to
  // the mobile menu when it's open). Sidebar remains the writer.
  const {
    projects, setProjects, loading, setLoading, activeWorkspaceId, setActiveWorkspaceId,
    isTreeOpen, setIsTreeOpen,
  } = useWorkspaceCtx();
  const [error, setError] = useState<string | null>(null);
  const [showCreateFlow, setShowCreateFlow] = useState(false);
  const [confirmPending, setConfirmPending] = useState<ConfirmPending>(null);

  // Lock (pin) the Tree open on mobile so forge selection doesn't auto-hide it.
  // Persisted so the preference survives reloads.
  const [isTreeLocked, setIsTreeLocked] = useState(
    () => localStorage.getItem(TREE_LOCK_KEY) === "1",
  );
  const toggleTreeLock = useCallback(() => {
    setIsTreeLocked((locked) => {
      const next = !locked;
      localStorage.setItem(TREE_LOCK_KEY, next ? "1" : "0");
      return next;
    });
  }, []);

  // The left menu is one rail with two states (design-mock hover/pin behavior):
  // collapsed → a slim icon strip (ProjectRail); expanded → the full panel
  // (workspace list + subject tree) floating over the canvas. On DESKTOP the
  // panel opens on hover OR when pinned (`exp = railPinned || railHover`); on
  // MOBILE (no hover) it's the tap-driven overlay tracked by `isTreeOpen`.
  const [railHovered, setRailHovered] = useState(false);
  const [railPinned, setRailPinned] = useState(
    () => localStorage.getItem(RAIL_PIN_KEY) === "1",
  );
  const togglePin = useCallback(() => {
    setRailPinned((pinned) => {
      const next = !pinned;
      localStorage.setItem(RAIL_PIN_KEY, next ? "1" : "0");
      return next;
    });
  }, []);
  const expandedDesktop = railPinned || railHovered;
  const expanded = isMobile ? isTreeOpen : expandedDesktop;

  // Keep WorkspaceCtx.isTreeOpen mirroring the desktop expand state so the canvas
  // onboarding guide (its only external reader) sees the same funnel state.
  useEffect(() => {
    if (!isMobile) setIsTreeOpen(expandedDesktop);
  }, [isMobile, expandedDesktop, setIsTreeOpen]);

  // Open the panel from an action (rail "+", create). Desktop pins it open so it
  // survives the pointer leaving; mobile flips the overlay flag.
  const openPanel = useCallback(() => {
    if (isMobile) {
      setIsTreeOpen(true);
    } else {
      setRailPinned(true);
      localStorage.setItem(RAIL_PIN_KEY, "1");
    }
  }, [isMobile, setIsTreeOpen]);

  // Expanded rail width. The mock rail is a fixed width (no drag handle), so the
  // single morphing rail just transitions between collapsed (58px) and this.
  const workspaceW = WORKSPACE_DEFAULT;

  useEffect(() => {
    setLoading(true);
    listProjects(getToken)
      .then(setProjects)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "Failed to load projects"))
      .finally(() => {
        setLoading(false);
        onProjectsLoaded?.();
      });
  }, [getToken, onProjectsLoaded, setLoading, setProjects]);

  // Auto-drill into the workspace that contains the active forge
  useEffect(() => {
    if (!forgeId || !projects.length) return;
    for (const p of projects) {
      for (const s of p.subjects) {
        if (s.forges.some((f) => f.id === forgeId)) {
          setActiveWorkspaceId(p.id);
          return;
        }
      }
    }
  }, [forgeId, projects, setActiveWorkspaceId]);

  // On mobile: auto-close Tree when a forge is selected — unless the user has
  // pinned it open.
  useEffect(() => {
    if (isMobile && forgeId && !isTreeLocked) setIsTreeOpen(false);
  }, [forgeId, isMobile, isTreeLocked]);

  const closeMobileOverlays = useCallback(() => {
    setIsTreeOpen(false);
  }, []);

  // Called on the actual forge tap (covers re-selecting the already-active forge,
  // which wouldn't change forgeId and so wouldn't trigger the effect above).
  const handleForgeSelect = useCallback(() => {
    if (isMobile && !isTreeLocked) setIsTreeOpen(false);
  }, [isMobile, isTreeLocked]);

  const activeProject = activeWorkspaceId
    ? (projects.find((p) => p.id === activeWorkspaceId) ?? null)
    : null;

  const handleCreateProject = useCallback(async (name: string) => {
    try {
      const project = await createProject(getToken, name);
      setProjects((prev) => [project, ...prev]);
      setActiveWorkspaceId(project.id);
      // Ensure the panel is expanded so the user can immediately add a forge to
      // the fresh workspace (pins on desktop, opens the overlay on mobile).
      openPanel();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create workspace");
    }
  }, [getToken, setProjects, setActiveWorkspaceId, openPanel]);

  const handleRenameProject = useCallback(async (projectId: string, name: string) => {
    try {
      await renameProject(getToken, projectId, name);
      setProjects((prev) => prev.map((p) => (p.id === projectId ? { ...p, name } : p)));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to rename workspace");
    }
  }, [getToken, setProjects]);

  const handleDeleteProject = useCallback((projectId: string) => {
    const project = projects.find((p) => p.id === projectId);
    setConfirmPending({
      title: "Delete workspace",
      message: `"${project?.name ?? "this workspace"}" and all its subjects and forges will be permanently deleted.`,
      action: async () => {
        await deleteProject(getToken, projectId);
        setProjects((prev) => prev.filter((p) => p.id !== projectId));
        if (activeWorkspaceId === projectId) setActiveWorkspaceId(null);
      },
    });
  }, [getToken, projects, activeWorkspaceId, setProjects, setActiveWorkspaceId]);

  const handleSetIcon = useCallback(async (projectId: string, icon: string) => {
    try {
      await setProjectIcon(getToken, projectId, icon);
      setProjects((prev) => prev.map((p) => (p.id === projectId ? { ...p, icon: icon || undefined } : p)));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to set icon");
    }
  }, [getToken, setProjects]);

  const handleCreateSubject = useCallback(async (projectId: string, title: string): Promise<ApiSubject> => {
    const subject = await createSubject(getToken, projectId, title);
    setProjects((prev) => prev.map((p) =>
      p.id === projectId ? { ...p, subjects: [...p.subjects, subject] } : p
    ));
    return subject;
  }, [getToken, setProjects]);

  const handleRenameSubject = useCallback(async (subjectId: string, title: string) => {
    try {
      await renameSubject(getToken, subjectId, title);
      setProjects((prev) => prev.map((p) => ({
        ...p,
        subjects: p.subjects.map((s) => (s.id === subjectId ? { ...s, title } : s)),
      })));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to rename subject");
    }
  }, [getToken, setProjects]);

  const handleDeleteSubject = useCallback((subjectId: string) => {
    let subjectTitle = "this subject";
    for (const p of projects) {
      const s = p.subjects.find((s) => s.id === subjectId);
      if (s) { subjectTitle = s.title; break; }
    }
    setConfirmPending({
      title: "Delete subject",
      message: `"${subjectTitle}" and all its forges will be permanently deleted.`,
      action: async () => {
        await deleteSubject(getToken, subjectId);
        setProjects((prev) => prev.map((p) => ({
          ...p,
          subjects: p.subjects.filter((s) => s.id !== subjectId),
        })));
      },
    });
  }, [getToken, projects, setProjects]);

  const handleCreateForge = useCallback(async (subjectId: string, title: string): Promise<ApiForge> => {
    const forge = await createForge(getToken, subjectId, title);
    setProjects((prev) => prev.map((p) => ({
      ...p,
      subjects: p.subjects.map((s) =>
        s.id === subjectId ? { ...s, forges: [...s.forges, forge] } : s
      ),
    })));
    return forge;
  }, [getToken, setProjects]);

  const handleRenameForge = useCallback(async (forgeId: string, title: string) => {
    try {
      await renameForge(getToken, forgeId, title);
      setProjects((prev) => prev.map((p) => ({
        ...p,
        subjects: p.subjects.map((s) => ({
          ...s,
          forges: s.forges.map((sc) => (sc.id === forgeId ? { ...sc, title } : sc)),
        })),
      })));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to rename forge");
    }
  }, [getToken, setProjects]);

  const handleDeleteForge = useCallback((forgeId: string) => {
    let forgeTitle = "this forge";
    for (const p of projects) {
      for (const s of p.subjects) {
        const f = s.forges.find((f) => f.id === forgeId);
        if (f) { forgeTitle = f.title; break; }
      }
    }
    setConfirmPending({
      title: "Delete forge",
      message: `"${forgeTitle}" and all its canvas content will be permanently deleted.`,
      action: async () => {
        await deleteForge(getToken, forgeId);
        setProjects((prev) => prev.map((p) => ({
          ...p,
          subjects: p.subjects.map((s) => ({
            ...s,
            forges: s.forges.filter((sc) => sc.id !== forgeId),
          })),
        })));
      },
    });
  }, [getToken, projects, setProjects]);

  const handleCreateFlow = useCallback(async (subjectName: string) => {
    if (!activeProject) return;
    setShowCreateFlow(false);
    try {
      const subject = await handleCreateSubject(activeProject.id, subjectName);
      const forge = await handleCreateForge(subject.id, "Blank Forge");
      useCanvasStore.getState().seedFreshForge(forge.id);
      setForgeId(forge.id);
      setDoc({
        forgeId: forge.id,
        title: forge.title,
        researchQuestion: forge.researchQuestion,
        projectId: activeProject.id,
        projectName: activeProject.name,
        siblingForges: [],
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create subject");
    }
  }, [activeProject, handleCreateSubject, handleCreateForge, setForgeId, setDoc]);

  const handleConfirm = useCallback(async () => {
    if (!confirmPending) return;
    const { action } = confirmPending;
    setConfirmPending(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    }
  }, [confirmPending]);

  const handleSelectProject = useCallback((projectId: string) => {
    setActiveWorkspaceId(projectId);
    // On mobile a selection from the collapsed strip expands the panel so the
    // user can pick a forge.
    if (isMobile) setIsTreeOpen(true);
  }, [isMobile, setActiveWorkspaceId, setIsTreeOpen]);

  // Collapsed rail "+" expands the panel so the user can create in the list.
  const handleNewWorkspace = useCallback(() => openPanel(), [openPanel]);

  // The subject tree + label reveals only when open; the collapsed 58px rail shows
  // just the workspace circles (mobile's overlay is always "open").
  const showTree = isMobile || expandedDesktop;

  // Shared panel body: pin/WORKSPACES header + workspace switcher + subject tree + footer.
  const panelBody = (
    <>
      {/* Header: pin toggle centred in the first 58px (so it sits exactly where the
          collapsed rail's pin is → the widen is seamless) + the "WORKSPACES" label. */}
      <div className={`flex items-center pt-3 pb-2.5 ${THEME.sidebar.borderBottom} shrink-0`}>
        {!isMobile ? (
          <div className="w-[58px] flex justify-center shrink-0">
            <button
              data-testid="panel-pin"
              onClick={togglePin}
              title={railPinned ? "Unpin menu" : "Keep menu open"}
              aria-label={railPinned ? "Unpin menu" : "Keep menu open"}
              aria-pressed={railPinned}
              className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors ${
                railPinned ? THEME.sidebar.pinActive : THEME.sidebar.pinIdle
              }`}
            >
              {railPinned ? <Pin size={15} /> : <ChevronsRight size={15} />}
            </button>
          </div>
        ) : (
          <span className="w-3 shrink-0" />
        )}
        <span className={`${THEME.sidebar.mapsLabel} flex-1`}>WORKSPACES</span>
      </div>

      {/* Workspace switcher — fills the collapsed rail; capped so the tree has room
          once expanded. */}
      <div className={`overflow-y-auto overflow-x-hidden ${THEME.sidebar.borderBottom} ${THEME.scrollbar.dark} ${showTree ? "shrink-0 max-h-[42%]" : "flex-1"}`}>
        <WorkspaceList
          projects={projects}
          loading={loading}
          error={null}
          activeId={activeWorkspaceId}
          onSelect={handleSelectProject}
          onCreate={handleCreateProject}
          onRename={handleRenameProject}
          onDelete={handleDeleteProject}
        />
      </div>

      {/* Subject tree for the active workspace — only when open (kept out of the
          slim collapsed rail). */}
      {showTree && (
      <div className="flex-1 min-h-0 overflow-hidden">
        {activeProject ? (
          <WorkspaceView
            project={activeProject}
            onRenameSubject={handleRenameSubject}
            onDeleteSubject={handleDeleteSubject}
            onCreateForge={handleCreateForge}
            onRenameForge={handleRenameForge}
            onDeleteForge={handleDeleteForge}
            showCreateFlow={showCreateFlow}
            onShowCreateFlow={() => setShowCreateFlow(true)}
            onHideCreateFlow={() => setShowCreateFlow(false)}
            onCreateFlow={handleCreateFlow}
            onForgeSelect={handleForgeSelect}
            isMobile={isMobile}
            isTreeLocked={isTreeLocked}
            onToggleLock={toggleTreeLock}
          />
        ) : (
          <p className="text-xs text-slate-500 px-4 py-6 text-center select-none">
            Select a workspace to see its subjects.
          </p>
        )}
      </div>
      )}

      {/* Footer: account (desktop — the top strip holds the brand, not this) */}
      {!isMobile && (
        <div className={`${THEME.sidebar.borderTop} px-3 py-2 shrink-0 flex items-center`}>
          <TaitoraUserButton />
        </div>
      )}
    </>
  );

  return (
    <div
      data-testid="sidebar-root"
      className="relative flex h-full"
      onMouseEnter={isMobile ? undefined : () => setRailHovered(true)}
      onMouseLeave={isMobile ? undefined : () => setRailHovered(false)}
    >
      {isMobile ? (
        <>
          {/* Mobile: slim collapsed strip + a tap-driven fixed overlay panel. */}
          <ProjectRail
            projects={projects}
            loading={loading}
            activeId={activeWorkspaceId}
            onSelect={handleSelectProject}
            onSetIcon={handleSetIcon}
            onNewWorkspace={handleNewWorkspace}
            isMobile={isMobile}
            pinned={railPinned}
            onTogglePin={togglePin}
            roundRight
          />
          <AnimatePresence initial={false}>
            {expanded && (
              <motion.div
                key="panel"
                className="fixed top-0 left-[58px] h-full z-[28] overflow-hidden shadow-xl"
                initial={reduce ? false : { width: 0 }}
                animate={{ width: MOBILE_TREE_W }}
                exit={{ width: 0 }}
                transition={reduce ? { duration: 0 } : { duration: SB.motion.panelDuration, ease: SB.motion.ease }}
              >
                <div
                  data-testid="tree-panel"
                  className={`shrink-0 overflow-hidden flex flex-col h-full ${THEME.sidebar.railSurface} rounded-r-2xl`}
                  style={{ width: MOBILE_TREE_W }}
                >
                  {panelBody}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          {expanded && (
            <div
              data-testid="mobile-backdrop"
              className="fixed inset-0 z-[22] bg-black/20"
              onClick={closeMobileOverlays}
            />
          )}
        </>
      ) : (
        <>
          {/* 58px spacer reserves the rail's width so the widening rail (an absolute
              overlay) never reflows the canvas. */}
          <div className="w-[58px] shrink-0" aria-hidden />
          {/* Single persistent morphing rail: ONE element whose width CSS-transitions
              between collapsed (58px) and expanded — no mount/unmount, one surface —
              so hover/unhover is a clean widen (never a zoom) and the colour is
              identical collapsed and expanded (mock rail). */}
          <div
            data-testid="tree-panel"
            data-expanded={expandedDesktop}
            className={`absolute left-0 top-0 h-full z-30 overflow-hidden flex flex-col ${THEME.sidebar.railSurface} ${THEME.sidebar.railSeam} rounded-r-2xl ${expandedDesktop ? THEME.sidebar.railExpandShadow : ""}`}
            style={{
              width: expandedDesktop ? workspaceW : SB.rail.collapsed,
              transition: reduce ? "none" : "width .3s cubic-bezier(.4,0,.2,1)",
            }}
          >
            {panelBody}
          </div>
        </>
      )}

      {confirmPending && (
        <ConfirmDialog
          title={confirmPending.title}
          message={confirmPending.message}
          onConfirm={handleConfirm}
          onCancel={() => setConfirmPending(null)}
        />
      )}

      {error && (
        <div className="absolute bottom-2 left-14 right-2 text-xs text-red-400 bg-red-900/20 rounded px-2 py-1 z-50">
          {error}
        </div>
      )}
    </div>
  );
}
